import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MenuItem, PrimeIcons, TreeNode } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { lastValueFrom } from 'rxjs';
import { TitleCasePipe } from '@angular/common';
import { CreateTreeLevelDialogComponent } from '../create-tree-level-dialog/create-tree-level-dialog.component';
import { RenameTreeLevelDialogComponent } from '../rename-tree-level-dialog/rename-tree-level-dialog.component';
import { DeleteTreeLevelDialogComponent } from '../delete-tree-level-dialog/delete-tree-level-dialog.component';
import { CreateDesignDialogComponent } from '../create-design-dialog/create-design-dialog.component';
import { getChildLevel } from '../well-explorer-helpers';
import { MoveItemDialogComponent } from '../move-item-dialog/move-item-dialog.component';
import { UsersService } from 'src/app/core/services/users-service';
import { SessionStorageKeys, StoreService } from 'src/app/core/services/store.service';
import { Router } from '@angular/router';

@Component({
    selector: 'app-well-explorer-item',
    templateUrl: './well-explorer-item.component.html',
    styleUrls: ['./well-explorer-item.component.scss'],
    providers: [DialogService],
    standalone: false
})
export class WellExplorerItemComponent {

  readonly companyLevel = 'companies';
  readonly designLevel = 'designs';

  private titleCasePipe = new TitleCasePipe();

  public hover: boolean;
  public hoverNode: TreeNode | null;
  public getChildLevel = getChildLevel;

  @Input() public isAdmin: boolean;
  @Input() public isGlobalAdmin: boolean;
  @Input() public treeNode: any;
  @Input() public selectedNode: TreeNode;
  @Input() public columns: any[];
  @Input() public rowData: any[];
  @Input() public treeItems: TreeNode[];
  @Input() public selectedDesignId: string;

  @Output() public contextMenuUpdated = new EventEmitter<MenuItem[]>();
  @Output() public nodeDeleted = new EventEmitter<TreeNode>();
  @Output() public nodeSelected = new EventEmitter<TreeNode>();
  @Output() public itemRenamed = new EventEmitter<{ name: string, treeNode: TreeNode }>();
  @Output() public designCloned = new EventEmitter<TreeNode>();
  @Output() public openDesignInNewTab = new EventEmitter<TreeNode>();
  @Output() public designExported = new EventEmitter<TreeNode>();
  @Output() public designMoved = new EventEmitter<TreeNode>();
  @Output() public projectMoved = new EventEmitter<any>();
  @Output() public siteMoved = new EventEmitter<any>();
  @Output() public wellMoved = new EventEmitter<any>();
  @Output() public wellboreMoved = new EventEmitter<any>();
  @Output() public importDesignDialogShow = new EventEmitter<TreeNode>();
  @Output() public childNodeAdded = new EventEmitter<{ levelData: { id: string, name: string }, level: string, treeNode: TreeNode }>();

  constructor(
    private _dialogService: DialogService,
    private _usersService: UsersService,
    private _store: StoreService,
    private _router: Router
  ) {}

  public addChildNode(treeNode: TreeNode): void {
    const level = getChildLevel(treeNode.data.level);
    const dialogReference = this._dialogService.open(level === this.designLevel ? CreateDesignDialogComponent as any : CreateTreeLevelDialogComponent, {
      data: { level, treeItems: this.treeItems },
      header: 'Create a New ' + this.titleCasePipe.transform(level.slice(0, -1)),
      width: '500px'
    });

    dialogReference.onClose.subscribe((levelData: { id: string, name: string }) => {
      if(levelData){
        this.childNodeAdded.emit({ levelData : levelData, treeNode, level: level});
      }
    })
  }

  public deleteItem(treeNode: TreeNode): void {
    const { level, label: levelName } = treeNode.data;

    const deleteDialogRef = this._dialogService.open(DeleteTreeLevelDialogComponent, {
      data: { levelName, parentName: treeNode?.parent?.data?.label ?? '', level },
      header: `Delete ${level === this.companyLevel ? 'Company' : this.titleCasePipe.transform(level.slice(0, -1))}`,
      width: '70%'
    });

    deleteDialogRef.onClose.subscribe(res => {
      if (res === 'confirmed') {
        this.nodeDeleted.emit(treeNode);
      }
    })
  }

  public cloneDesign(treeNode: TreeNode): void {
    this.designCloned.emit(treeNode);
  }

  public exportDesign(treeNode: TreeNode): void {
    this.designExported.emit(treeNode);
  }

  public moveItem(treeNode: TreeNode, targetLevel: string, labelText: string): void {
    const { label: itemName, id } = treeNode.data;
    const fromParentId = treeNode.parent.data.id;
    const moveRef = this._dialogService.open(MoveItemDialogComponent, {
      data: { 
        id, 
        fromParentId, 
        currentLevelName: itemName, 
        treeItems: this.treeItems,
        targetLevel,
        labelText
      },
      header: `Move - ${itemName}`,
      width: '30%'
    });

    moveRef.onClose.subscribe(res => {
      if (res) {
        if (treeNode.data.level === this.designLevel) {
          this.designMoved.emit(res);
          } else if (treeNode.data.level === 'projects') {
            this.projectMoved.emit(res);
          } else if (treeNode.data.level === 'sites') {
            this.siteMoved.emit(res);
          } else if (treeNode.data.level === 'wells') {
            this.wellMoved.emit(res);
          } else if (treeNode.data.level === 'wellbores') {
            this.wellboreMoved.emit(res);
          }
        }
      })
  }

  public renameItem(treeNode: TreeNode): void {
    const { level, label: currentLevelName } = treeNode.data;
    const singularLevel = level === this.companyLevel ? 'Company' : this.titleCasePipe.transform(level.slice(0, -1));

    const ref = this._dialogService.open(RenameTreeLevelDialogComponent, {
      data: { level, currentLevelName, singularLevel },
      header: `Rename ${singularLevel}`,
      width: '400px'
    });

    ref.onClose.subscribe((levelName: string) => {
      if (levelName) {
        this.itemRenamed.emit({ name: levelName, treeNode });
      }
    })
  }

  public onMouseOver(treeNode: TreeNode): void {
    this.hover = true;
    this.hoverNode = treeNode;
    if (treeNode['node'].data) {
      this.selectedNode = treeNode['node'];
    }
  }

  public onMouseLeave(): void {
    this.hover = false;
    this.hoverNode = null;
  }

  public filterContextMenu(level: string): void {
    let actions: MenuItem[] = [
      { label: 'Delete', icon: PrimeIcons.TRASH, command: () => this.deleteItem(this.selectedNode) },
      { label: 'Rename', icon: PrimeIcons.PENCIL, command: () => this.renameItem(this.selectedNode) }
    ];
    if ((level === 'companies' || level === 'projects' || level === 'sites') && (!this.isAdmin || !this.isGlobalAdmin)) {
      actions = actions.filter(action => action.label !== 'Delete');
    }

    if (level === 'designs') {
      if (this.selectedDesignId !== this.selectedNode.data.id) {
        actions.unshift({ label: 'Open in new tab', icon: PrimeIcons.EXTERNAL_LINK, command: () => this.openDesignInNewTab.emit(this.selectedNode) });
        actions.unshift({ label: 'Open Design', icon: PrimeIcons.FOLDER_OPEN, command: () => this.nodeSelected.emit(this.selectedNode) });
      }
      actions.push(
        { label: 'Clone', icon: PrimeIcons.COPY, command: () => this.cloneDesign(this.selectedNode) },
        { label: 'Export', icon: PrimeIcons.DOWNLOAD, command: () => this.exportDesign(this.selectedNode) },
        { label: 'Move', icon: PrimeIcons.UPLOAD, command: () => this.moveItem(this.selectedNode, 'wellbores', 'wellbore') },
      );

      if (this.selectedDesignId == this.selectedNode.data.id) {
        actions.push({ label: 'Close Design', icon: PrimeIcons.TIMES, command: () => this.closeDesign() });
      }
      // if (!this.isAdmin || !this.isCompanyAdmin || !this.isGlobalAdmin) {
      if (!this.isAdmin || !this.isGlobalAdmin) {
        actions = actions.filter(action => action.label !== 'Delete');
      }
    } else {
      actions.push({ label: 'Add', icon: PrimeIcons.PLUS, command: () => this.addChildNode(this.selectedNode) });
    }

    // Add Move option for all levels except companies (admins only)
    if (level === 'projects' && (this.isAdmin || this.isGlobalAdmin)) {
      actions.push({ label: 'Move', icon: PrimeIcons.ARROW_RIGHT, command: () => this.moveItem(this.selectedNode, 'companies', 'company') });
    }

    if (level === 'sites' && (this.isAdmin || this.isGlobalAdmin)) {
      actions.push({ label: 'Move', icon: PrimeIcons.ARROW_RIGHT, command: () => this.moveItem(this.selectedNode, 'projects', 'project') });
    }

    if (level === 'wells' && (this.isAdmin || this.isGlobalAdmin)) {
      actions.push({ label: 'Move', icon: PrimeIcons.ARROW_RIGHT, command: () => this.moveItem(this.selectedNode, 'sites', 'site') });
    }

    if (level === 'wellbores') {
      actions.push({ label: 'Import', icon: PrimeIcons.UPLOAD, command: () => this.importDesignDialogShow.emit(this.selectedNode) });
      if (this.isAdmin || this.isGlobalAdmin) {
        actions.push({ label: 'Move', icon: PrimeIcons.ARROW_RIGHT, command: () => this.moveItem(this.selectedNode, 'wells', 'well') });
      }
    }

    this.contextMenuUpdated.emit(actions);
  }

  public async closeDesign(): Promise<void> {
    this.selectedNode = null;
    await lastValueFrom(this._usersService.updateCurrentAppIds({ }));
    this._store.setSession(SessionStorageKeys.APP_IDS, null);
    this._router.navigate(['/designs'], { replaceUrl: true });
  }

  public getWellExplorerLevelName(level: string): string {
    return level == 'companies' ? 'Company' : this.titleCasePipe.transform(level.slice(0, -1));
  }

  public getReducedIndentRowNode(treeNode: any): any {
    return {
      ...treeNode,
      level: Number(treeNode?.level ?? 0) * 0.6
    };
  }

  public getLevelPillClass(level: string): string {
    switch (level) {
      case 'companies':
        return 'pill-company';
      case 'projects':
        return 'pill-project';
      case 'sites':
        return 'pill-site';
      case 'wells':
        return 'pill-well';
      case 'wellbores':
        return 'pill-wellbore';
      case 'designs':
        return 'pill-design';
      default:
        return 'pill-default';
    }
  }
}
